

import Layout from 'components/ui/Layout';
import { useAppointment } from 'features/appointment/hooks/useAppointment';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  MessageSquare,
  User,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDateTime } from 'utils/dateFormatter';

const ViewAppointmentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: response, isLoading, isError, error } =
    useAppointment(id);

  const appointment = response?.data;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/10 text-yellow-600',
      confirmed: 'bg-blue-500/10 text-blue-600',
      completed: 'bg-green-500/10 text-green-600',
      cancelled: 'bg-red-500/10 text-red-600',
      rescheduled: 'bg-orange-500/10 text-orange-600',
      rejected: 'bg-gray-500/10 text-gray-600',
      no_show: 'bg-red-700/10 text-red-700',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600';
  };

  // Loading
  if (isLoading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-screen'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
        </div>
      </Layout>
    );
  }

  // Error
  if (isError) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-destructive text-lg mb-4'>
            Error loading appointment
          </div>
          <p className='text-muted-foreground mb-4'>
            {error?.message || 'Something went wrong'}
          </p>
          <button
            onClick={() => navigate('/appointments')}
            className='px-4 py-2 bg-primary text-primary-foreground rounded-md'
          >
            Back to Appointments
          </button>
        </div>
      </Layout>
    );
  }

  // Not found
  if (!appointment) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-lg mb-4'>Appointment not found</div>
          <button
            onClick={() => navigate('/appointments')}
            className='px-4 py-2 bg-primary text-primary-foreground rounded-md'
          >
            Back to Appointments
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='min-h-screen bg-background'>
        {/* HEADER */}
        <div className='bg-card border-b border-border sticky top-0 z-10'>
          <div className='container mx-auto px-4 py-4'>
            <div className='flex items-center space-x-4'>
              <button
                onClick={() => navigate('/appointment')}
                className='p-2 hover:bg-muted rounded-md'
              >
                <ArrowLeft size={20} />
              </button>

              <div>
                <h1 className='text-2xl font-bold flex items-center gap-2'>
                  <Calendar className='text-primary' size={24} />
                  Appointment Details
                </h1>
                <p className='text-sm text-muted-foreground'>
                  #{appointment.appointmentNumber}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className='container mx-auto px-4 py-6'>
          <div className='bg-card border rounded-lg p-6 space-y-6'>
            
            {/* STATUS */}
            <div>
              <span
                className={`px-3 py-1 text-xs rounded-full ${getStatusColor(
                  appointment.status
                )}`}
              >
                {appointment.status?.toUpperCase()}
              </span>
            </div>

            {/* CUSTOMER + SELLER */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              {/* Customer */}
              {/* <div className='bg-muted p-4 rounded-lg'>
                <div className='flex items-center gap-3'>
                  <User size={16} />
                  <span className='text-xs text-muted-foreground'>
                    Customer
                  </span>
                </div>

                <p className='text-sm font-medium mt-2'>
                  {appointment.customerId?.phoneNumber || 'N/A'}
                </p>
              </div> */}

                <div className='space-y-2 bg-muted rounded-lg p-4'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <User size={14} />
                  <span>Customer Information</span>
                </label>
                <div className='flex items-center space-x-3'>
                  {appointment.customerId?.profilePhoto ? (
                    <img
                      src={appointment.customerId.profilePhoto}
                      alt='Seller'
                      className='w-12 h-12 rounded-full object-cover border border-border'
                    />
                  ) : (
                    <div className='w-12 h-12 rounded-full bg-purple-500/10 border border-border flex items-center justify-center'>
                      <User size={24} />
                    </div>
                  )}
                  <div>
                    <p className='text-sm font-medium'>
                      {appointment.customerId?.phoneNumber || 'N/A'}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      {appointment.customerId?.email || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Seller */}
              {/* <div className='bg-muted p-4 rounded-lg'>
                <div className='flex items-center gap-3'>
                  <User size={16} />
                  <span className='text-xs text-muted-foreground'>
                    Seller
                  </span>
                </div>

                <p className='text-sm font-medium mt-2'>
                  {appointment.sellerId?.phoneNumber || 'N/A'}
                </p>
              </div> */}
                <div className='space-y-2 bg-muted rounded-lg p-4'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <User size={14} />
                  <span>Seller Information</span>
                </label>
                <div className='flex items-center space-x-3'>
                  {appointment.sellerId?.profilePhoto ? (
                    <img
                      src={appointment.sellerId.profilePhoto}
                      alt='Seller'
                      className='w-12 h-12 rounded-full object-cover border border-border'
                    />
                  ) : (
                    <div className='w-12 h-12 rounded-full bg-purple-500/10 border border-border flex items-center justify-center'>
                      <User size={24} />
                    </div>
                  )}
                  <div>
                    <p className='text-sm font-medium'>
                      {appointment.sellerId?.phoneNumber || 'N/A'}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      {appointment.sellerId?.email || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

             

            {/* TYPE */}
            <div className='bg-muted p-4 rounded-lg'>
              <p className='text-xs text-muted-foreground'>Type</p>
              <p className='text-sm font-medium capitalize'>
                {appointment.type || '--'}
              </p>
            </div>

            {/* Tour description */}
            {appointment?.type === "tour" ? (
              <div className='bg-muted p-4 rounded-lg'>
                <p className='text-xs text-muted-foreground'>Description</p>
                <p className='text-sm font-bold capitalize pb-1'>
                  {appointment?.tourId?.title || '--'}
                </p>
                <p className='text-sm font-medium capitalize'>
                  {appointment?.tourId?.description || '--'}
                </p>
              </div>
            ) : (
              <div className='bg-muted p-4 rounded-lg'>
                <p className='text-xs text-muted-foreground'>Property Location</p>
                <p className='text-sm font-medium capitalize'>
                  {appointment?.propertyId?.propertyLocation || 'No description available'}
                </p>
              </div>
            )}

            {/* BOOKING TYPE */}
            {/* <div className='bg-muted p-4 rounded-lg'>
              <p className='text-xs text-muted-foreground'>Booking Type</p>
              <p className='text-sm font-medium capitalize'>
                {appointment.booking_type || '--'}
              </p>
            </div> */}

            

            {/* SCHEDULE */}
            <div className='bg-muted p-4 rounded-lg'>
              <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                <Calendar size={14} />
                Schedule
              </div>

              <p className='text-sm font-medium mt-2'>
                 {formatDateTime(appointment.scheduledDate)?.split(',')[0]}
              </p>

              <p className='text-s text-muted-foreground'>
                {appointment.scheduledTime || '--'}
              </p>
            </div>

            {/* NOTES */}
            {(appointment.customerNotes ||
              appointment.sellerNotes ||
              appointment.adminNotes) && (
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                {appointment.customerNotes && (
                  <div className='bg-blue-500/5 p-4 rounded-lg'>
                    <div className='flex items-center gap-2 text-xs text-blue-600'>
                      <MessageSquare size={14} />
                      Customer Notes
                    </div>
                    <p className='text-sm mt-2'>
                      {appointment.customerNotes}
                    </p>
                  </div>
                )}

                {appointment.sellerNotes && (
                  <div className='bg-purple-500/5 p-4 rounded-lg'>
                    <div className='flex items-center gap-2 text-xs text-purple-600'>
                      <MessageSquare size={14} />
                      Seller Notes
                    </div>
                    <p className='text-sm mt-2'>
                      {appointment.sellerNotes}
                    </p>
                  </div>
                )}

                {appointment.adminNotes && (
                  <div className='bg-gray-500/5 p-4 rounded-lg'>
                    <div className='flex items-center gap-2 text-xs text-gray-600'>
                      <MessageSquare size={14} />
                      Admin Notes
                    </div>
                    <p className='text-sm mt-2'>
                      {appointment.adminNotes}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Timestamps */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border'>
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <Clock size={14} />
                  <span>Created At</span>
                </label>
                <p className='text-sm'>{formatDateTime(appointment.createdAt)}</p>
              </div>

              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <Clock size={14} />
                  <span>Last Updated</span>
                </label>
                <p className='text-sm'>{formatDateTime(appointment.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ViewAppointmentPage;
